import { html, css, LitElement } from 'lit-element';
import { db } from './intrivity_app.js'; // Ensure this exports your Firestore instance
import { collection, getDocs, doc, query, setDoc, deleteDoc, where, orderBy, updateDoc, arrayUnion, arrayRemove } from "firebase/firestore";
import { getStorage, ref, getDownloadURL } from "firebase/storage";
import { getFunctions, httpsCallable } from "firebase/functions";
import uuidv4 from 'v4-uuid';
import './intrivity-admin-invitations.js';
import './intrivity-user-search.js';
import './user_edit_dialog.js';
import './badge.js';
import { storage } from './intrivity_app.js'

// Import necessary MWC components
import '@material/mwc-button';
import '@material/mwc-dialog';
import '@material/mwc-textfield';
import '@material/mwc-list/mwc-list.js';
import '@material/mwc-list/mwc-list-item.js';
import '@material/mwc-snackbar';
import '@material/mwc-checkbox';
import '@material/mwc-formfield';

import { INTRIVITY_CLIENTS_GROUPS_CLOSED } from './intrivity-admin-clients.js';
import { 
    emitEvent, loadJSONFromStorage, getJSONFromStorage, getScore, hasRole,
    INTRIVITY_LOAD_RESOURCES, INTRIVITY_LOAD_CLIENTS, INTRIVITY_LOAD_GROUPS, 
    INTRIVITY_GROUP_REPORTS_LOADED, INTRIVITY_GOOGLE_LOGGED_IN, 
    INTRIVITY_GOOGLE_LOGGED_OUT, INTRIVITY_LOGIN, INTRIVITY_LOGGED_IN, 
    INTRIVITY_LOGOUT, INTRIVITY_NAV_SELECT, INTRIVITY_PRINTABLE_TOGGLE, 
    INTRIVITY_GROUP_SELECT, INTRIVITY_MASQUERADE, invitationName
} from './utility.js'
import { t, Localized } from './i18n.js';
import { recordAudit, AUDIT } from './audit_log.js';

export class IntrivityAdminUsers extends Localized(LitElement) {
  static get properties() {
    return {
      users: { type: Array },
      user_profile: { type: Object },
      client: { type: Object },
      group: { type: Object },
      mode: { type: String }, // 'pm' when viewed through the Program Manager panel
      _editUserDialogOpen: { type: Boolean },
      _editingUser: { type: Object },
      _isLoading: { type: Boolean },
      _errorMessage: { type: String },
      _feedbackMessage: { type: String },
      _teamUuid: { type: String },              // selected group for invitations
      _rolesDialogOpen: { type: Boolean },
      _rolesUser: { type: Object },       // user being edited
      _rolesForm: { type: Object }       // { roles: [], docId: null }
    };
  }

  constructor() {
    super();
    this.users = [];
    this._editUserDialogOpen = false;
    this._editingUser = null;
    this._isLoading = false;
    this._errorMessage = '';
    this._feedbackMessage = '';
    this._rolesDialogOpen = false;
    this._rolesUser = null;
    this._rolesForm = { role: [], docId: null };
  }

    // Load users for the group
    async _loadGroupUsers(group) {
        console.log(`Loading users for group ID: ${group.uuid}`);
        const groupsRef = collection(db, "users");
        const q = query(
            groupsRef,
            where("groups", "array-contains", group.uuid)
        );

        try {
            const snapshot = await getDocs(q);
            const _users = snapshot.docs.map(doc => doc.data());
            console.log(_users);
            this.users = _users;
        } catch (error) {
            console.error("Error loading users for " + group.uuid + ":", error);
            return [];
        }
    } 
    
  _generateRandomPassword(length = 16) {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()_-+=';
    let pw = '';
    for (let i = 0; i < length; i++) {
      pw += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return pw;
  }
  
  // Call Firebase Functions createMoodleUser (httpsCallable)
  async _createMoodleUser(user) {
    // permission guard (optional)
    if (!hasRole('superadmin', this.user_profile)) {
      this._showFeedback(t('usersAdmin.noMoodlePermission'));
      return;
    }

    const userId = user.email || user.uid;
    if (!userId) {
      this._showFeedback(t('usersAdmin.noUid'));
      return;
    }

    // build payload
    const payload = {
      username: user.email || userId,
      password: this._generateRandomPassword(),
      firstname: user.firstName || user.firstName || (user.displayName ? user.displayName.split(' ')[0] : ''),
      lastname: user.lastName || (user.displayName ? user.displayName.split(' ').slice(1).join(' ') : ''),
      email: user.email || userId
    };

    try {
      this._showFeedback(t('usersAdmin.creatingMoodle', { name: payload.username }));

      // get functions instance (uses default Firebase app)
      const functions = getFunctions();
      const createMoodleUserFn = httpsCallable(functions, 'createMoodleUser');

      // call the cloud function
      const result = await createMoodleUserFn(payload);

      // result.data should be the JSON object returned by your function
      console.log('createMoodleUser result:', result);
      const returned = result.data;

      // show returned JSON (stringified) in feedback and console
      this._showFeedback(t('usersAdmin.moodleCreated', { name: payload.username }));
      // optionally show full response to the user (alert or snackbar)
      alert(`createMoodleUser response:\n${JSON.stringify(returned, null, 2)}`);
    } catch (err) {
      console.error('Error calling createMoodleUser:', err);
      this._showFeedback(t('usersAdmin.moodleError'));
      // if the function returned useful error info, show it
      if (err && err.message) {
        console.error(err.message);
      }
    }
  }
  
  _generateRandomCode() {
    return Array.from({ length: 8 }, () => Math.random().toString(36)[2].toUpperCase()).join('');
  }

  _openEditDialog(user) {
    this._editingUser = user;
    this._editUserDialogOpen = true;
  }

  async _openRolesDialog(user) {
    if (!this.group || !this.group.uuid) {
      this._showFeedback(t('invitations.noGroup'));
      return;
    }
    this._rolesDialogOpen = true;
    this._rolesUser = user;
    // reset form
    this._rolesForm = { role: [], docId: null };

    // try to load existing group_roles for this user + group
    try {
      const rolesRef = collection(db, "group_roles");
      const q = query(
        rolesRef,
        where("email", "==", user.email || user.uid),
        where("group_uuid", "==", this.group.uuid)
      );

      const snap = await getDocs(q);
      if (!snap.empty) {
        // assume one doc per (email, group)
        const docSnap = snap.docs[0];
        const data = docSnap.data();
        this._rolesForm = {
          role: Array.isArray(data.role) ? data.role.slice() : [],
          docId: docSnap.id
        };
      } else {
        // no roles doc yet
        this._rolesForm = { role: [], docId: null };
      }
    } catch (err) {
      console.error("Error loading group_roles:", err);
      this._showFeedback(t('usersAdmin.rolesLoadError'));
    }
  }

  _closeRolesDialog() {
    this._rolesDialogOpen = false;
    this._rolesUser = null;
    this._rolesForm = { role: [], docId: null };
  }

  _rolesToggle(role, e) {
    const checked = e.target.checked;
    const rolesSet = new Set(this._rolesForm.role || []);
    if (checked) rolesSet.add(role);
    else rolesSet.delete(role);
    this._rolesForm = { ...this._rolesForm, role: Array.from(rolesSet) };
  }

  // ensure role
  async _saveRoles() {
    if (!hasRole('superadmin', this.user_profile) && !hasRole('admin', this.user_profile)) {
      this._showFeedback(t('usersAdmin.noRolesPermission'));
      return;
    }
    if (!this._rolesUser) {
      this._showFeedback(t('usersAdmin.noUser'));
      return;
    }
    const email = this._rolesUser.email || this._rolesUser.uid;
    if (!email) {
      this._showFeedback(t('usersAdmin.noUid'));
      return;
    }
    const roles = Array.isArray(this._rolesForm.role) ? this._rolesForm.role.filter(r => r) : [];
    // Snapshot for the audit trail before anything is written (F-06).
    const rolesBefore = Array.isArray(this._rolesUser.role) ? [...this._rolesUser.role] : [];
    // Roles managed by this dialog. A role is only stripped from the user doc
    // once none of their group_roles grant it anymore (checked below).
    const MANAGED_ROLES = ['trainer', 'program_manager', 'admin'];

    try {
      const rolesCollection = collection(db, "group_roles");

      if (roles.length === 0) {
        // remove existing doc if present
        if (this._rolesForm.docId) {
          const docRef = doc(db, "group_roles", this._rolesForm.docId);
          await deleteDoc(docRef);
          this._showFeedback(t('usersAdmin.rolesRemoved', { name: this._rolesUser.displayName || email }));
        } else {
          this._showFeedback(t('usersAdmin.noRolesToRemove', { name: this._rolesUser.displayName || email }));
        }
      } else {
        // set or update doc
        if (this._rolesForm.docId) {
          const docRef = doc(db, "group_roles", this._rolesForm.docId);
          await updateDoc(docRef, {
            role: roles,
            updated_at: new Date().toISOString()
          });
        } else {
          // create new doc with generated id
          const newId = uuidv4();
          const docRef = doc(db, "group_roles", newId);
          await setDoc(docRef, {
            email: email,
            group_uuid: this.group.uuid,
            role: roles,
            created_at: new Date().toISOString()
          });
        }
        this._showFeedback(t('usersAdmin.rolesUpdated', { name: this._rolesUser.displayName || email }));
      }

      // Reconcile the user's managed roles against every group that grants them,
      // so a role is only removed from the user if no other group still has it checked.
      if (this._rolesUser) {
        const allRolesSnap = await getDocs(query(rolesCollection, where("email", "==", email)));
        const grantedRoles = new Set();
        allRolesSnap.forEach(d => {
          const r = d.data().role;
          if (Array.isArray(r)) r.forEach(x => grantedRoles.add(x));
        });

        const currentUserRoles = new Set(this._rolesUser.role || []);
        let do_update = false;
        for (const role of MANAGED_ROLES) {
          if (grantedRoles.has(role) && !currentUserRoles.has(role)) {
            currentUserRoles.add(role);
            do_update = true;
          } else if (!grantedRoles.has(role) && currentUserRoles.has(role)) {
            currentUserRoles.delete(role);
            do_update = true;
          }
        }
        if (do_update) {
          this._rolesUser = { ...this._rolesUser, role: Array.from(currentUserRoles) };
          await setDoc(doc(db, "users", this._rolesUser.email), this._rolesUser, { merge: true });
        }
      }
      recordAudit(AUDIT.GROUP_ROLE_CHANGE, {
        subject: email,
        detail: {
          group: this.group && this.group.uuid,
          groupName: this.group && this.group.name,
          rolesRequested: roles,
          globalRolesBefore: rolesBefore,
          globalRolesAfter: Array.isArray(this._rolesUser.role) ? [...this._rolesUser.role] : [],
        },
      });
    } catch (err) {
      console.error("Error saving group_roles:", err);
      this._showFeedback(t('usersAdmin.rolesSaveError'));
    } finally {
      this._closeRolesDialog();
      // refresh UI to reflect changed roles
      this._loadGroupUsers(this.group);
    }
  }

  // Helper to show whether user currently has role for UI badges/indicators if you want to extend later
  _userHasRole(user, roleName) {
    // This function relies on group_roles collection for precise truth, so we keep it simple
    // If your user objects already embed role info you can check that here.
    return false;
  }

  async _createTestUsers() {
    if (hasRole('superadmin', this.user_profile)) {
      let x = 0;
      for (let x=1; x < 1801; x++) {
         let email = 'user' + x + '@example.com'; 
         try {  
            const userRef = doc(db, "users", email);
            let body = {};
            body.email = email;
            body.displayName = 'Sample ' + x + ' User';
            body.firstName = 'Sample ' + x;
            body.lastName = 'User';
            body.fullName = body.displayName;
            body.group = 'test_example_group';
            body.groups = [];
            body.groups.push("test_example_group");
            body.paid = true;
            body.process = false;
            body.processing = false;
            body.profile = {};
            body.survey_id = '402421430';
            // load report
            const storageRef = ref(storage, "reports/" + email + ".json");
            await getDownloadURL(storageRef)
               .then(url => fetch(url))
               .then(response => response.json())
               .then(data => {  
                   body.profile.m = data.m.average;
                   body.profile.r = data.r.average;
                   body.profile.t = data.t.average;
                   body.profile.s = data.s.average;
               });
            console.log(body);
            await setDoc(userRef, body, { merge: true }); 
                

          } catch (error) {
            console.log(error);
            console.error("Error saving user:", email);
          }
      }
    }
  }      
  
  _showFeedback(message) {
    this._feedbackMessage = message;
    alert(message);
//    this.shadowRoot.getElementById('feedback-snackbar').open();
  }

  static get styles() {
    return css`
      .group-item {
        width: 100%;
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
        align-items: flex-start;
        gap: 8px;
        padding-bottom: 10px;
        border-bottom: 1px solid #e0e0e0;
      }
      .group-item > div:first-child {
        flex: 1 1 220px;
        min-width: 0;
      }
      .actions {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        flex: 0 0 auto;
        gap: 4px;
      }
      .actions mwc-button {
        margin-left: 0;
        --mdc-typography-button-font-size: 0.72rem;
        --mdc-button-height: 28px;
      }
        .group-name {
            font-weight: bold;
        }
        .client-institution {
            font-size: 0.9em;
            color: #555;
        }
         .group-description, .client-address {
             font-size: 0.85em;
             color: #6b6b6b;
        }
    `;
  }

  firstUpdated(props) {
    if (this.group) {
        this._loadGroupUsers(this.group);
    } 
  }
  
  openUserSearch() {
    const dialog = document.createElement('intrivity-user-search');
    document.body.appendChild(dialog);

    // listen for user selection and add the user to the current group
    dialog.addEventListener('user-selected', async (e) => {
      const selectedUser = e.detail.user;
      try {
        await this._addUserToGroup(selectedUser);
      } catch (err) {
        console.error('Error adding user to group:', err);
        this._showFeedback(t('usersAdmin.addError', { name: selectedUser.displayName || selectedUser.email }));
      } finally {
        // cleanup: close/remove the dialog component if it implements a close()
        if (typeof dialog.close === 'function') dialog.close();
        // remove element from DOM to avoid duplicates
        if (dialog.parentNode) dialog.parentNode.removeChild(dialog);
      }
    });

    dialog.open();        
  }
  
    async _addUserToGroup(user) {
      // sanity: need a group selected
      if (!this.group || !this.group.uuid) {
        this._showFeedback(t('invitations.noGroup'));
        return;
      }

      // don't add if already in group
      if (user.groups &&Array.isArray(user.groups) && user.groups.includes(this.group.uuid)) {
        this._showFeedback(t('usersAdmin.alreadyInGroup'))
        return;
      }

      // require an identifier for the user (you use email as doc id elsewhere)
      const userId = user.email || user.uid;
      if (!userId) {
        this._showFeedback(t('usersAdmin.noUid'));
        return;
      }

      const userRef = doc(db, 'users', userId);

      try {
        // Try to update with arrayUnion (will create field if it exists, won't duplicate)
        await updateDoc(userRef, {
          groups: arrayUnion(this.group.uuid),
          updated_at: new Date().toISOString()
        });

        // Create or update invitation for user if needed
        try {
          console.log("Creating/updating invitation for user:", user.email);
          console.log(user)
          let invitation = {};
          invitation.uuid = user.email;
          const { first, last } = invitationName(user);
          invitation.first = first;
          invitation.last = last;
          invitation.email = user.email;
          invitation.group_uuid = this.group.uuid;
          invitation.code = 'CODEUSED_' + this._generateRandomCode();
          invitation.status = 'claimed';
          invitation.cc = 'No';
          invitation.claimed_by = user.email;
          invitation.claimed = new Date().toISOString();

          const subColRef = doc(
            db,
            'groups',
            this.group.uuid,
            'invitations',
            invitation.uuid
          );

          await setDoc(subColRef, invitation, { merge: true });

        } catch (error) {
          console.error('Error creating/updating invitation:', error);
        }


        // If updateDoc succeeded but the document didn't exist, updateDoc throws.
        // To be robust: check existence and set if not exists
      } catch (updateErr) {
        // If document doesn't exist, create it with minimal data and the groups array
        console.warn('updateDoc failed, attempting to create user document:', updateErr);

        // Build a minimal doc from the selected user
        const newUserDoc = {
          email: user.email || null,
          displayName: user.displayName || user.fullName || '',
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          groups: [ this.group.uuid ],
          created_at: new Date().toISOString(),
          // preserve other known fields if present:
          ...(user.profile ? { profile: user.profile } : {}),
          ...(user.image ? { image: user.image } : {}),
        };

        await setDoc(userRef, newUserDoc, { merge: true });
      }

      await updateDoc(doc(db, 'groups', this.group.uuid), { status: 'process' });
      this._showFeedback(t('usersAdmin.added', { name: user.displayName || userId, group: this.group.name || this.group.uuid }));

      // refresh UI list
      this._loadGroupUsers(this.group);
    }

    async _removeUserFromGroup(user) {
        
        
      if (!this.group || !this.group.uuid) {
        this._showFeedback(t('invitations.noGroup'));
        return;
      }

      const userId = user.email || user.uid;
      if (!userId) {
        this._showFeedback(t('usersAdmin.noUid'));
        return;
      }

      const userRef = doc(db, "users", userId);

      try {
        await updateDoc(userRef, {
          groups: arrayRemove(this.group.uuid),
          updated_at: new Date().toISOString()
        });

        // Remove any group_roles docs for this user + group
        try {
          const rolesRef = collection(db, "group_roles");
          const q = query(
            rolesRef,
            where("group_uuid", "==", this.group.uuid),
            where("email", "==", userId)
          );

          const snap = await getDocs(q);

          const deletes = snap.docs.map(d =>
            deleteDoc(doc(db, "group_roles", d.id))
          );

          await Promise.all(deletes);
        } catch (rolesErr) {
          console.error("Error removing group_roles for user:", rolesErr);
        }
        
        await updateDoc(doc(db, 'groups', this.group.uuid), { status: 'process' });
        this._showFeedback(t('usersAdmin.removed', { name: user.displayName || userId, group: this.group.name }));

      } catch (err) {
        console.error("Error removing group from user:", err);
        this._showFeedback(t('usersAdmin.removeError', { group: this.group.name }));
      }

      // refresh group users list
      this._loadGroupUsers(this.group);
    }

  render() {
      
    return html`
      ${this.group && this.group.type == 'team' ? html`` : html`<mwc-button outlined label=${t('usersAdmin.addUser')} @click=${() => this.openUserSearch()}></mwc-button>`}

      <mwc-list>
          ${this.users.sort((a, b) => (a.lastName ?? '').localeCompare(b.lastName ?? '') || (a.firstName ?? '').localeCompare(b.firstName ?? '')).map((top, n) => html`
          <mwc-list-item style="display: block; width: auto; height: auto;">
          <div style="display: grid; grid-template-columns: 5px 1fr; background-color: ${n%2 == 0 ? 'white': 'lightgray'};">
            <div>
                ${top.profilex ? html`<intrivity-badge-plain .m=${getScore(top.profile.m)} .r=${getScore(top.profile.r)} .s=${getScore(top.profile.s)} .t=${getScore(top.profile.t)} size="0.5"></intrivity-badge-plain>`: html``}                                           
            </div>
            <div class="group-item">
              <div style="min-width: 0;">
                <div class="group-name">
                    <strong>${top.displayName}</strong> 
                    ${top.image ? html`<img alt="" width="20px;" src="${top.image}" />`: html``}
                </div>
                <div class="group-description">${top.email || t('clients.noEmail')}</div>
                <div class="group-description">${top.description || t('groups.noDescription')}</div>
              </div>
              <div class="actions">
                ${hasRole('superadmin', this.user_profile) ? html`
                  <mwc-button dense outlined label=${t('usersAdmin.roles')} @click=${() => this._openRolesDialog(top)}></mwc-button>
                `: html``}
                ${hasRole('adminxxx', this.user_profile) || hasRole('superadminxxxx', this.user_profile) ? html`
                  <mwc-button dense outlined label=${t('usersAdmin.createMoodle')} @click=${() => {
                      if (confirm(t('usersAdmin.confirmMoodle', { name: top.displayName || top.email }))) {
                        this._createMoodleUser(top);
                      }
                  }}></mwc-button>
                `: html``}
                <mwc-button dense outlined label=${t('common.remove')} @click=${() => {
                        if (confirm(t('usersAdmin.confirmRemove', { name: top.displayName }))) { this._removeUserFromGroup(top);}
                    }}></mwc-button>
                ${(this.group && this.group.type == 'team') || this.mode == 'pm' ? html`` : html`
                  <mwc-button dense outlined label=${t('common.edit')} @click=${() => this._openEditDialog(top)}></mwc-button>
                `}
              </div>
            </div>
          </div>
          </mwc-list-item>

        `)}    
      </mwc-list>

      <intrivity-user-edit-dialog
        .user_profile=${this.user_profile}
        .editingUser=${this._editingUser}
        .open=${this._editUserDialogOpen}
        @user-saved=${() => this._loadGroupUsers(this.group)}
        @dialog-closed=${() => { this._editUserDialogOpen = false; }}
      ></intrivity-user-edit-dialog>

      <!-- NEW: Roles dialog -->
      <mwc-dialog heading=${t('usersAdmin.manageRoles')} ?open=${this._rolesDialogOpen} @closed=${() => this._closeRolesDialog()}>
        <div style="display: grid; gap: 8px; min-width: 320px;">
          <div><strong>${this._rolesUser ? this._rolesUser.displayName || this._rolesUser.email : ''}</strong></div>

          <mwc-formfield label=${t('role.trainer')}>
            <mwc-checkbox
              .checked=${(this._rolesForm.role || []).includes('trainer')}
              @change=${e => this._rolesToggle('trainer', e)}>
            </mwc-checkbox>
          </mwc-formfield>
        
          <mwc-formfield label=${t('role.programManager')}>
            <mwc-checkbox
              .checked=${(this._rolesForm.role || []).includes('program_manager')}
              @change=${e => this._rolesToggle('program_manager', e)}>
            </mwc-checkbox>
          </mwc-formfield>

          <mwc-formfield label=${t('role.admin')}>
            <mwc-checkbox
              .checked=${(this._rolesForm.role || []).includes('admin')}
              @change=${e => this._rolesToggle('admin', e)}>
            </mwc-checkbox>
          </mwc-formfield>

          <div style="display:flex; gap:8px; justify-content:flex-end; margin-top: 12px;">
            <mwc-button label=${t('common.cancel')} dialogAction="cancel" @click=${() => this._closeRolesDialog()}></mwc-button>
            <mwc-button label=${t('common.save')} unelevated @click=${this._saveRoles}></mwc-button>
          </div>
        </div>
      </mwc-dialog>

      <mwc-snackbar id="feedback-snackbar" labelText=${this._feedbackMessage}></mwc-snackbar>
    `;
  }
}

customElements.define('intrivity-admin-users', IntrivityAdminUsers);
